import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

type CognitoApiErrorBody = {
  __type?: unknown;
  message?: unknown;
};

function cognitoEndpoint(authority: string): string {
  const issuer = new URL(authority);
  return `${issuer.protocol}//${issuer.host}/`;
}

async function cognitoRequest<T>(
  authority: string,
  target: string,
  body: Record<string, unknown>,
): Promise<T> {
  const response = await fetch(cognitoEndpoint(authority), {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  });
  if (response.ok) return (await response.json()) as T;
  const error = (await response.json().catch(() => ({}))) as CognitoApiErrorBody;
  throw new Error(typeof error.message === 'string' ? error.message : 'Cognito request failed.');
}

export async function associateSoftwareToken(
  authority: string,
  accessToken: string,
): Promise<string> {
  const result = await cognitoRequest<{ SecretCode?: string }>(
    authority,
    'AssociateSoftwareToken',
    {
      AccessToken: accessToken,
    },
  );
  if (!result.SecretCode) throw new Error('Cognito did not return an authenticator secret.');
  return result.SecretCode;
}

export async function verifySoftwareToken(
  authority: string,
  accessToken: string,
  code: string,
): Promise<void> {
  await cognitoRequest(authority, 'VerifySoftwareToken', {
    AccessToken: accessToken,
    UserCode: code,
    FriendlyDeviceName: 'quality.vet authenticator',
  });
  await cognitoRequest(authority, 'SetUserMFAPreference', {
    AccessToken: accessToken,
    EmailMfaSettings: { PreferredMfa: false },
    SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
  });
}

export async function setEmailMfa(
  authority: string,
  accessToken: string,
  enabled: boolean,
): Promise<void> {
  await cognitoRequest(authority, 'SetUserMFAPreference', {
    AccessToken: accessToken,
    EmailMfaSettings: { Enabled: enabled, PreferredMfa: enabled },
    SoftwareTokenMfaSettings: { PreferredMfa: false },
  });
}

export async function disableAllMfa(authority: string, accessToken: string): Promise<void> {
  await cognitoRequest(authority, 'SetUserMFAPreference', {
    AccessToken: accessToken,
    EmailMfaSettings: { Enabled: false, PreferredMfa: false },
    SoftwareTokenMfaSettings: { Enabled: false, PreferredMfa: false },
  });
}

type EmailMfaAuthenticationResult = {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
};

type EmailMfaChallengeResult = {
  AuthenticationResult?: EmailMfaAuthenticationResult;
};

export type MfaChallenge = {
  challengeName: 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP' | 'EMAIL_MFA';
  challengeSession: string;
};

function sessionFromAuthenticationResult(
  authentication: EmailMfaAuthenticationResult | undefined,
  message: string,
): CognitoUserSession {
  if (!authentication?.AccessToken || !authentication.IdToken) throw new Error(message);
  return new CognitoUserSession({
    AccessToken: new CognitoAccessToken({ AccessToken: authentication.AccessToken }),
    IdToken: new CognitoIdToken({ IdToken: authentication.IdToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: authentication.RefreshToken ?? '' }),
  });
}

type MfaSelectionResult = {
  ChallengeName?: string;
  Session?: string;
};

export async function selectMfaChallenge({
  authority,
  clientId,
  username,
  challengeSession,
  method,
}: {
  authority: string;
  clientId: string;
  username: string;
  challengeSession: string;
  method: 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP';
}): Promise<MfaChallenge> {
  const result = await cognitoRequest<MfaSelectionResult>(authority, 'RespondToAuthChallenge', {
    ChallengeName: 'SELECT_MFA_TYPE',
    ChallengeResponses: { USERNAME: username, ANSWER: method },
    ClientId: clientId,
    Session: challengeSession,
  });
  if (
    (result.ChallengeName !== 'SOFTWARE_TOKEN_MFA' &&
      result.ChallengeName !== 'EMAIL_OTP' &&
      result.ChallengeName !== 'EMAIL_MFA') ||
    !result.Session
  ) {
    throw new Error('Cognito did not start the selected MFA challenge. Start the login again.');
  }
  return { challengeName: result.ChallengeName, challengeSession: result.Session };
}

export async function completeEmailMfaChallenge({
  authority,
  clientId,
  username,
  challengeName,
  challengeSession,
  code,
}: {
  authority: string;
  clientId: string;
  username: string;
  challengeName: 'EMAIL_OTP' | 'EMAIL_MFA';
  challengeSession: string;
  code: string;
}): Promise<CognitoUserSession> {
  const codeKey = challengeName === 'EMAIL_MFA' ? 'EMAIL_MFA_CODE' : 'EMAIL_OTP_CODE';
  const result = await cognitoRequest<EmailMfaChallengeResult>(
    authority,
    'RespondToAuthChallenge',
    {
      ChallengeName: challengeName,
      ChallengeResponses: { USERNAME: username, [codeKey]: code },
      ClientId: clientId,
      Session: challengeSession,
    },
  );
  return sessionFromAuthenticationResult(
    result.AuthenticationResult,
    'Cognito did not complete the email MFA challenge. Start the login again.',
  );
}

export async function completeAuthenticatorMfaChallenge({
  authority,
  clientId,
  username,
  challengeSession,
  code,
}: {
  authority: string;
  clientId: string;
  username: string;
  challengeSession: string;
  code: string;
}): Promise<CognitoUserSession> {
  const result = await cognitoRequest<EmailMfaChallengeResult>(
    authority,
    'RespondToAuthChallenge',
    {
      ChallengeName: 'SOFTWARE_TOKEN_MFA',
      ChallengeResponses: { USERNAME: username, SOFTWARE_TOKEN_MFA_CODE: code },
      ClientId: clientId,
      Session: challengeSession,
    },
  );
  return sessionFromAuthenticationResult(
    result.AuthenticationResult,
    'Cognito did not complete the authenticator MFA challenge. Start the login again.',
  );
}

export async function changeCognitoPassword(
  authority: string,
  accessToken: string,
  previousPassword: string,
  proposedPassword: string,
): Promise<void> {
  await cognitoRequest(authority, 'ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

export async function beginEmailChange(
  authority: string,
  accessToken: string,
  email: string,
): Promise<void> {
  await cognitoRequest(authority, 'UpdateUserAttributes', {
    AccessToken: accessToken,
    UserAttributes: [{ Name: 'email', Value: email }],
  });
}

export async function verifyEmailChange(
  authority: string,
  accessToken: string,
  code: string,
): Promise<void> {
  await cognitoRequest(authority, 'VerifyUserAttribute', {
    AccessToken: accessToken,
    AttributeName: 'email',
    Code: code,
  });
}

export type CurrentSessionSummary = {
  authenticatedAt?: Date;
  expiresAt?: Date;
};

export function currentSessionSummary(accessToken: string): CurrentSessionSummary {
  const payload = accessToken.split('.')[1];
  if (!payload) return {};
  try {
    const decoded = JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/'))) as Record<
      string,
      unknown
    >;
    return {
      ...(typeof decoded.auth_time === 'number'
        ? { authenticatedAt: new Date(decoded.auth_time * 1000) }
        : {}),
      ...(typeof decoded.exp === 'number' ? { expiresAt: new Date(decoded.exp * 1000) } : {}),
    };
  } catch {
    return {};
  }
}

export function hasRecentAuthentication(accessToken: string, maximumAgeMinutes = 10): boolean {
  const { authenticatedAt } = currentSessionSummary(accessToken);
  return Boolean(
    authenticatedAt && Date.now() - authenticatedAt.getTime() <= maximumAgeMinutes * 60_000,
  );
}
